"use client";

import React, { Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { Box, Paper, Typography, alpha } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useOrder } from "src/contexts";
import { useOrderQuery } from "src/hooks/useOrder";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { useBundleCards, useOrderBundles } from "src/hooks/useBundles";
import { useBundlesEnabled } from "src/utils/bundles-enabled";
import { bundleContext, orderBundleFields } from "src/utils/bundles";

/**
 * "Za paket »Spomini«" with the slot line under it, an info box at the top of
 * a slot's target page's content column,
 * when the link carried `bundle` (slotHref) and that bundle is the selected or
 * an applied one. Nothing otherwise.
 */
function Strip({ sx }: { sx?: SxProps<Theme> }) {
  const searchParams = useSearchParams();
  const locale = useLocale();
  const translate = useAppTranslate();
  const enabled = useBundlesEnabled();
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const { data: view } = useOrderBundles(
    mainOrderId,
    guestToken,
    order?.amount,
    locale,
  );
  const currency = order?.currency || view?.currency || "eur";
  const { data: cards } = useBundleCards(currency, locale);
  const ctx = bundleContext({
    bundleId: searchParams?.get("bundle"),
    slotId: searchParams?.get("slot"),
    view: enabled ? view : null,
    applied: orderBundleFields(order).appliedBundles,
    cards,
    locale,
  });
  if (!ctx) return null;
  // The drawer's paket block, in an info tint: outlined, rounded, icon left.
  return (
    <Paper
      variant="outlined"
      data-testid="bundle-context-strip"
      sx={[
        (theme) => ({
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          p: 1.5,
          borderRadius: 2,
          borderColor: alpha(theme.palette.info.main, 0.4),
          bgcolor: alpha(theme.palette.info.main, 0.08),
          textAlign: "left",
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Inventory2OutlinedIcon
        fontSize="small"
        sx={{ color: "info.main", mt: "2px" }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {translate("bundles.contextFor", { name: ctx.name })}
        </Typography>
        {ctx.slot && (
          <Typography variant="body2" color="text.secondary">
            {translate("bundles.slotLine", {
              quantity: String(ctx.slot.quantity),
              label: ctx.slot.label,
            })}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/** Suspense-wrapped: useSearchParams on a statically rendered page needs it. */
export default function BundleContextStrip({
  sx,
}: {
  /** Placement (margins) in the page's content column. */
  sx?: SxProps<Theme>;
}) {
  return (
    <Suspense fallback={null}>
      <Strip sx={sx} />
    </Suspense>
  );
}
